import { useCallback, useMemo, useRef, useState } from "react";
import { EditQuizButtonState } from "../editQuiz";

type ModalWrapperButtonType = "back" | "next" | "close" | "save" | "saveProfile";

const INITIAL_BUTTON_STATES: Record<ModalWrapperButtonType, EditQuizButtonState> = {
	back: { enabled: true },
	next: { enabled: true },
	save: { enabled: false },
	close: { enabled: true },
	saveProfile: { enabled: true },
};

export default function useEditQuizButton() {
	const [buttonStates, setButtonStates] = useState(INITIAL_BUTTON_STATES);

	const handleButtonChanges = useCallback((key: ModalWrapperButtonType, changes: Partial<EditQuizButtonState>) => {
		// Only update state if something changed to avoid max depth reached error for useEffect
		setButtonStates((prev) => {
			const changeKeys = Object.keys(changes) as Array<keyof EditQuizButtonState>;
			const isIdentical = changeKeys.every((changeKey) => prev[key][changeKey] === changes[changeKey]);
			if (isIdentical) {
				return prev;
			}

			return {
				...prev,
				[key]: { ...prev[key], ...changes },
			};
		});
	}, []);

	const buttonHandlers = useMemo(
		() => ({
			onBackEnabledChange: (changes: Partial<EditQuizButtonState>) => handleButtonChanges("back", changes),
			onNextEnabledChange: (changes: Partial<EditQuizButtonState>) => handleButtonChanges("next", changes),
			onSaveEnabledChange: (changes: Partial<EditQuizButtonState>) => handleButtonChanges("save", changes),
			onSaveProfileChange: (changes: Partial<EditQuizButtonState>) => handleButtonChanges("saveProfile", changes),
		}),
		[handleButtonChanges],
	);

	const refCallbacksRegistry = useRef<
		Partial<Record<ModalWrapperButtonType, (node: HTMLButtonElement | null) => void>>
	>({});

	// Gets ref of button. In callback so it doesn't cause re-renders
	const getButtonRefHandler = useCallback(
		(key: ModalWrapperButtonType) => {
			if (!refCallbacksRegistry.current[key]) {
				refCallbacksRegistry.current[key] = (node: HTMLButtonElement | null) => {
					handleButtonChanges(key, { element: node });
				};
			}
			return refCallbacksRegistry.current[key];
		},
		[handleButtonChanges],
	);

	return {
		handleButtonChanges,
		buttonHandlers,
		getButtonRefHandler,
		backButton: buttonStates.back,
		nextButton: buttonStates.next,
		closeButton: buttonStates.close,
		saveButton: buttonStates.save,
		saveProfileButton: buttonStates.saveProfile,
	};
}
